(() => {
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'summary',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  class AcLayerManager {
    #abortController = new AbortController();
    #inertRoots = [];
    #layers = new Map();
    #scrollPosition = null;
    #unlockFrame = 0;

    constructor() {
      document.documentElement.dataset.acInputMode = 'pointer';
      document.addEventListener('pointerdown', this.#handlePointerInput, { capture: true, signal: this.#abortController.signal });
      document.addEventListener('keydown', this.#handleKeyboardInput, { capture: true, signal: this.#abortController.signal });
      document.addEventListener('keydown', this.#handleKeydown, { signal: this.#abortController.signal });
    }

    /**
     * @param {string} id
     * @param {{opener?: Element|null, getFocusRoots?: () => Array<Element|null>, getInertRoots?: () => Array<Element>, onEscape?: (event: KeyboardEvent) => void}} options
     */
    acquire(id, options = {}) {
      if (!id) return;
      const firstLayer = this.#layers.size === 0 && this.#unlockFrame === 0;
      window.cancelAnimationFrame(this.#unlockFrame);
      this.#unlockFrame = 0;
      // Reinserting brings an existing layer to the top of the ordered Map.
      this.#layers.delete(id);
      this.#layers.set(id, options);
      if (firstLayer) this.#lockPage(options);
    }

    /** @param {string} id @param {{restoreFocus?: boolean}} options */
    release(id, { restoreFocus = true } = {}) {
      const layer = this.#layers.get(id);
      if (!layer) return;
      const wasTopLayer = this.#topLayerId() === id;
      this.#layers.delete(id);

      if (this.#layers.size > 0) {
        if (restoreFocus && wasTopLayer) {
          const activeLayer = this.#layers.get(this.#topLayerId());
          const focusable = this.#focusableElements(activeLayer.getFocusRoots?.() || []);
          const target = focusable.includes(layer.opener) ? layer.opener : focusable[0];
          target?.focus({ preventScroll: true });
        }
        return;
      }
      window.cancelAnimationFrame(this.#unlockFrame);
      this.#unlockFrame = window.requestAnimationFrame(() => {
        this.#unlockFrame = 0;
        if (this.#layers.size > 0) return;
        this.#unlockPage();
        if (restoreFocus && wasTopLayer && layer.opener instanceof HTMLElement) {
          layer.opener.focus({ preventScroll: true });
        }
      });
    }

    isLocked() {
      return this.#layers.size > 0;
    }

    #topLayerId() {
      return [...this.#layers.keys()].at(-1);
    }

    #handlePointerInput = () => {
      document.documentElement.dataset.acInputMode = 'pointer';
    };

    #handleKeyboardInput = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (/^(Tab|Enter| |Escape|Arrow(?:Up|Down|Left|Right)|Home|End|PageUp|PageDown)$/.test(event.key)) {
        document.documentElement.dataset.acInputMode = 'keyboard';
      }
    };

    destroy = () => {
      window.cancelAnimationFrame(this.#unlockFrame);
      this.#abortController.abort();
      this.#layers.clear();
      this.#unlockPage();
      delete document.documentElement.dataset.acInputMode;
      delete window.AcLayerManager;
    };

    #handleKeydown = (event) => {
      const activeLayer = this.#layers.get(this.#topLayerId());
      if (!activeLayer) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        activeLayer.onEscape?.(event);
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = this.#focusableElements(activeLayer.getFocusRoots?.() || []);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1);

      if (!focusable.includes(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    #focusableElements(roots) {
      const elements = [];
      for (const root of roots) {
        if (!(root instanceof Element)) continue;
        if (root.matches(focusableSelector)) elements.push(root);
        elements.push(...root.querySelectorAll(focusableSelector));
      }
      return [...new Set(elements)].filter((element) => (
        !element.hidden
        && !element.closest('[hidden], [inert]')
        && (!element.closest('details:not([open])') || element.matches('details:not([open]) > summary'))
        && element.getClientRects().length > 0
      ));
    }

    #lockPage(options) {
      // An in-content drawer must isolate sibling branches, never its own ancestor.
      const roots = options.getInertRoots?.() || [...document.querySelectorAll('#MainContent, .shopify-section-group-footer-group')];
      this.#inertRoots = [...new Set(roots)]
        .map((root) => ({ root, wasInert: root.inert }));
      // Lenis reset reads scroll geometry. Do it before invalidating layout.
      window.AcSmoothScroll?.stop?.();
      this.#scrollPosition = { x: window.scrollX, y: window.scrollY };
      // A fixed body leaves document flow. Preserve its extent so native scrollY
      // and scroll-linked scenes do not reset to the top while a layer is open.
      document.documentElement.style.setProperty('--ac-layer-document-height', `${document.documentElement.scrollHeight}px`);
      document.body.style.setProperty('--ac-layer-scroll-top', `${-this.#scrollPosition.y}px`);
      for (const { root } of this.#inertRoots) root.inert = true;
      document.body.classList.add('ac-layer-open');
      document.dispatchEvent(new CustomEvent('ac:layer:change', { detail: { locked: true } }));
    }

    #unlockPage() {
      for (const { root, wasInert } of this.#inertRoots) root.inert = wasInert;
      this.#inertRoots = [];
      document.body.classList.remove('ac-layer-open');
      document.body.style.removeProperty('--ac-layer-scroll-top');
      document.documentElement.style.removeProperty('--ac-layer-document-height');
      if (this.#scrollPosition) {
        window.scrollTo({ left: this.#scrollPosition.x, top: this.#scrollPosition.y, behavior: 'instant' });
        this.#scrollPosition = null;
      }
      // Restart only after the original document geometry and position are restored.
      window.AcSmoothScroll?.start?.();
      document.dispatchEvent(new CustomEvent('ac:layer:change', { detail: { locked: false } }));
    }
  }

  if (!window.AcLayerManager) window.AcLayerManager = new AcLayerManager();
})();
